import { Chart, useChart } from '@chakra-ui/charts';
import { Box, HStack, Text } from '@chakra-ui/react';
import type { ChartDataPoint } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { formatChartAmount, formatChartDay } from './chart-locale-format';
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

type ChartSeries = { name: 'revenue' | 'commissions'; color: string; label?: string };

// Blue = chart / data-viz color. Revenue leads (darker), commissions trails (lighter).
const DEFAULT_SERIES: ChartSeries[] = [
  { name: 'revenue', color: 'blue.600' },
  { name: 'commissions', color: 'blue.300' },
];

interface RevenueCommissionChartProps {
  data?: ChartDataPoint[];
  currency?: string;
  isLoading?: boolean;
  series?: ChartSeries[];
}

export const RevenueCommissionChart: React.FC<RevenueCommissionChartProps> = ({
  data,
  currency = 'EUR',
  isLoading = false,
  series = DEFAULT_SERIES,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const seriesLabels: Record<ChartSeries['name'], string> = {
    revenue: t('dashboard.stats.attributedRevenue'),
    commissions: t('dashboard.stats.totalCommissions'),
  };
  const labelledSeries = series.map((item) => ({
    ...item,
    label: item.label ?? seriesLabels[item.name],
  }));

  const chartData =
    data?.map((point) => ({
      date: point.date,
      revenue: point.revenue,
      commissions: point.commissions,
    })) || [];

  const chart = useChart({ data: chartData, series: labelledSeries });

  if (isLoading || !data) {
    return (
      <Box
        height="400px"
        bg="bg.muted"
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="fg.muted"
      >
        {t('dashboard.stats.chartLoading')}
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box
        height="400px"
        bg="bg.muted"
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="fg.muted"
      >
        {t('dashboard.stats.noChartData')}
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Chart.Root height="320px" chart={chart}>
        <AreaChart data={chart.data} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
          {/* Subtle gradient fill (kept light, not a heavy wash) */}
          {chart.series.map((item) => (
            <defs key={item.name}>
              <Chart.Gradient
                id={`${item.name}-gradient`}
                stops={[
                  { offset: '0%', color: item.color, opacity: 0.14 },
                  { offset: '100%', color: item.color, opacity: 0 },
                ]}
              />
            </defs>
          ))}

          {/* Light, warm horizontal grid only */}
          <CartesianGrid stroke={chart.color('border')} vertical={false} strokeDasharray="2 7" />

          <XAxis
            dataKey={chart.key('date')}
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={28}
            tick={{ fill: chart.color('fg.muted'), fontSize: 11 }}
            tickFormatter={(value) => formatChartDay(value, locale)}
          />

          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            width={52}
            domain={[0, 'auto']}
            tick={{ fill: chart.color('fg.muted'), fontSize: 11 }}
            tickFormatter={(value) => formatChartAmount(value, currency, locale, { compact: true })}
          />

          <Tooltip
            cursor={{ stroke: chart.color('border'), strokeWidth: 1 }}
            animationDuration={100}
            content={
              <Chart.Tooltip
                labelFormatter={(value) => formatChartDay(value, locale)}
                formatter={(value) => formatChartAmount(Number(value), currency, locale)}
              />
            }
          />

          {/* Squared legend swatches (Wispr style), not round dots */}
          <Legend
            content={() => (
              <HStack justify="center" gap={5} pt={3}>
                {chart.series.map((s) => (
                  <HStack key={s.name} gap={2}>
                    <Box boxSize="11px" borderRadius="4px" bg={chart.color(s.color)} />
                    <Text fontSize="xs" color="fg.muted">
                      {s.label ?? s.name}
                    </Text>
                  </HStack>
                ))}
              </HStack>
            )}
          />

          {chart.series.map((item) => (
            <Area
              key={item.name}
              type="monotone"
              isAnimationActive={false}
              dataKey={chart.key(item.name)}
              fill={`url(#${item.name}-gradient)`}
              stroke={chart.color(item.color)}
              strokeWidth={2}
              strokeLinecap="round"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              baseValue={0}
            />
          ))}
        </AreaChart>
      </Chart.Root>
    </Box>
  );
};
